import axios from 'axios';
import { globalURLS } from '../../Global/urls.js';

export type SynapseListKey =
    | 'partners'
    | 'teams'
    | 'projects'
    | 'sessions'
    | 'users';

export interface SynapseTeamRef {
    id: string;
    name?: string | null;
    partnerid?: string | null;
    partner_name?: string | null;
}

export interface SynapsePartner {
    id: string;
    name?: string | null;
    logourl?: string | null;
}

export interface SynapseTeam {
    id: string;
    name?: string | null;
    partnerid?: string | null;
    partner_name?: string | null;
    logourl?: string | null;
}

export interface SynapseProject {
    id: string;
    name?: string | null;
    description?: string | null;
    bannerurl?: string | null;
    teams?: SynapseTeamRef[];
}

export interface SynapseSession {
    prim: number;
    project_id: string;
    session_id: string;
    project_name?: string | null;
    session_name?: string | null;
    session_status?: string | null;
    startdate?: number | string | null;
    enddate?: number | string | null;
}

export interface SynapseUser {
    id: string;
    name?: string | null;
    email?: string | null;
    partnerid?: string | null;
    profilepic?: string | null;
    partner?: SynapsePartner | null;
    teams?: SynapseTeamRef[];
}

export interface SynapseOverview {
    partners: SynapsePartner[];
    teams: SynapseTeam[];
    projects: SynapseProject[];
    sessions: SynapseSession[];
    users: SynapseUser[];
}

export interface SynapseDiscussionOption {
    discussionid: string;
    name: string;
    status?: string;
    startdate?: number | string;
}

export type SynapseEntityItem =
    | SynapsePartner
    | SynapseTeam
    | SynapseProject
    | SynapseSession
    | SynapseUser;

const get = async <T>(endpoint: string): Promise<T> => {
    const response = await axios.get(
        `${globalURLS.baseURL}${endpoint}`,
        globalURLS.config,
    );

    if (!response.data?.success) {
        throw new Error(response.data?.message || 'Request failed');
    }

    return response.data.data as T;
};

const post = async <T>(
    endpoint: string,
    body: Record<string, unknown>,
): Promise<T> => {
    const response = await axios.post(
        `${globalURLS.baseURL}${endpoint}`,
        body,
        globalURLS.config,
    );

    if (!response.data?.success) {
        throw new Error(response.data?.message || 'Request failed');
    }

    return response.data.data as T;
};

export const getSynapseOverview = () => get<SynapseOverview>('syn_mgmt_overview');

export const getSynapseDiscussions = (search?: string) => {
    const query = search?.trim()
        ? `?search=${encodeURIComponent(search.trim())}`
        : '';
    return get<SynapseDiscussionOption[]>(`syn_mgmt_discussions${query}`);
};

export const createSynapseItem = (
    entity: SynapseListKey,
    body: Record<string, unknown>,
) => post(`syn_mgmt_${entity.slice(0, -1)}_create`, body);

export const updateSynapseItem = (
    entity: SynapseListKey,
    body: Record<string, unknown>,
) => post(`syn_mgmt_${entity.slice(0, -1)}_update`, body);

export const deleteSynapseItem = (
    entity: SynapseListKey,
    body: Record<string, unknown>,
) => post(`syn_mgmt_${entity.slice(0, -1)}_delete`, body);
